import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { formConfig } from "../../formConfig";
import { buildDefaultValues, FormValues } from "../../schema";
import FieldRenderer from "../../components/FieldRenderer";
import ReadOnlySubmission from "../../components/ReadOnlySubmission";
import RecipientEditor from "../../components/RecipientEditor";
import {
  getSubmission,
  updateSubmission,
  setSubmissionProgress,
  getProgressStages,
  deleteSubmission,
  restoreSubmissionVersion,
  emailSubmission,
  AdminSubmission,
  ProgressStage
} from "../../api/admin";
import { usePermissions } from "../../auth/permissions";
import {
  exportPdf,
  printSubmission,
  exportExcel,
  exportWord,
  exportHtml,
  exportJson
} from "../../export/exportSubmission";

export default function AdminDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const submissionDate = searchParams.get("submissionDate") ?? undefined;
  const startInEdit = searchParams.get("edit") === "1";
  const perms = usePermissions();

  const [sub, setSub] = useState<AdminSubmission | null>(null);
  const [stages, setStages] = useState<ProgressStage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(startInEdit);
  const [showHistory, setShowHistory] = useState(false);

  // Email modal state
  const [showEmail, setShowEmail] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [emailNote, setEmailNote] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);

  const methods = useForm<FormValues>({ defaultValues: buildDefaultValues() as FormValues });

  useEffect(() => {
    if (!id) return;
    getSubmission(id, submissionDate)
      .then((s) => {
        setSub(s);
        methods.reset({ ...(buildDefaultValues() as FormValues), ...(s.data as FormValues) });
      })
      .catch((e) => setError(e.message ?? "Failed to load"));
    getProgressStages().then(setStages);
  }, [id, submissionDate]);

  const onSave = async (values: FormValues) => {
    if (!id || !sub) return;
    setError(null);
    setSavedMessage(null);
    try {
      await updateSubmission(id, sub.submissionDate, values as Record<string, unknown>);
      const refreshed = await getSubmission(id, sub.submissionDate);
      setSub(refreshed);
      setSavedMessage("Saved at " + new Date().toLocaleTimeString());
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  };

  const cancelEdit = () => {
    if (sub) methods.reset({ ...(buildDefaultValues() as FormValues), ...(sub.data as FormValues) });
    setEditing(false);
  };

  const data = (sub?.data ?? {}) as Record<string, unknown>;

  const doExport = async (fmt: "pdf" | "print" | "excel" | "word" | "html" | "json") => {
    if (!sub) return;
    try {
      if (fmt === "pdf") exportPdf(data);
      else if (fmt === "print") printSubmission(data);
      else if (fmt === "excel") exportExcel(data);
      else if (fmt === "word") await exportWord(data);
      else if (fmt === "html") exportHtml(data);
      else if (fmt === "json") exportJson(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const toggleStage = async (stageId: string, done: boolean) => {
    if (!id || !sub) return;
    try {
      const updated = await setSubmissionProgress(id, sub.submissionDate, stageId, done);
      setSub(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    }
  };

  const onDelete = async () => {
    if (!id || !sub) return;
    if (!window.confirm("Delete this submission permanently? This cannot be undone.")) return;
    try {
      await deleteSubmission(id, sub.submissionDate);
      navigate("/admin");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const onRestoreVersion = async (versionIndex: number) => {
    if (!id || !sub) return;
    if (!window.confirm("Restore this version? The current answers will be saved to history first.")) return;
    try {
      const updated = await restoreSubmissionVersion(id, sub.submissionDate, versionIndex);
      setSub(updated);
      methods.reset({ ...(buildDefaultValues() as FormValues), ...(updated.data as FormValues) });
      setSavedMessage("Version restored at " + new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Restore failed");
    }
  };

  const sendEmail = async () => {
    if (!id || !sub) return;
    setEmailBusy(true);
    setEmailStatus(null);
    try {
      const result = await emailSubmission(id, sub.submissionDate, emailRecipients, emailNote.trim() || undefined);
      setEmailStatus(`Sent to ${result.recipients.join(", ")}${result.cc.length ? `; Cc ${result.cc.join(", ")}` : ""}.`);
      setTimeout(() => {
        setShowEmail(false);
        setEmailStatus(null);
        setEmailRecipients([]);
        setEmailNote("");
      }, 900);
    } catch (e) {
      setEmailStatus(e instanceof Error ? e.message : "Send failed");
    } finally {
      setEmailBusy(false);
    }
  };

  if (!sub && !error) return <div>Loading…</div>;
  if (error && !sub) return <div className="banner-error">{error}</div>;
  if (!sub) return null;

  const projectName = data?.projectName as string | undefined;
  const jobNumber = data?.jobNumber as string | undefined;
  const history = sub.history ?? [];
  const canEdit = perms.has("submissions:edit");
  const canDelete = perms.has("submissions:delete");

  return (
    <div className="admin-detail">
      {error && <div className="banner-error">{error}</div>}
      <div className="admin-detail-header">
        <div>
          <Link to="/admin" className="back-link">← All submissions</Link>
          <h1>{projectName || "(untitled)"} {jobNumber && <span className="job-num">#{jobNumber}</span>}</h1>
          <div className="hint">
            Submitted by <strong>{sub.submittedBy?.userDetails}</strong> on {new Date(sub.submittedAt).toLocaleString()}
            {sub.editedAt && (<> · Last edited by {sub.editedBy?.userDetails} on {new Date(sub.editedAt).toLocaleString()}</>)}
          </div>
        </div>
      </div>
      <div className="admin-detail-toolbar screen-only export-toolbar">
        <button type="button" className="btn-secondary" onClick={() => doExport("pdf")}>PDF</button>
        <button type="button" className="btn-secondary" onClick={() => doExport("print")}>Print</button>
        <button type="button" className="btn-secondary" onClick={() => doExport("excel")}>Excel</button>
        <button type="button" className="btn-secondary" onClick={() => doExport("word")}>Word</button>
        <button type="button" className="btn-secondary" onClick={() => doExport("html")}>HTML</button>
        <button type="button" className="btn-secondary" onClick={() => doExport("json")}>JSON</button>
        <button type="button" className="btn-secondary" onClick={() => { setShowEmail(true); setEmailStatus(null); }}>✉ Email</button>
        {history.length > 0 && (
          <button type="button" className="btn-secondary" onClick={() => setShowHistory((h) => !h)}>
            History ({history.length})
          </button>
        )}
        {!editing && canEdit && (
          <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>Edit answers</button>
        )}
        {canDelete && (
          <button type="button" className="btn-danger" onClick={onDelete}>Delete</button>
        )}
        {savedMessage && <span className="hint">{savedMessage}</span>}
      </div>

      {showHistory && history.length > 0 && (
        <div className="history-panel">
          <h3>Edit history</h3>
          <ul className="history-list">
            {history.map((h, idx) => (
              <li key={idx} className="history-item">
                <span>
                  {new Date(h.savedAt).toLocaleString()}
                  {h.savedBy?.userDetails ? ` · ${h.savedBy.userDetails}` : ""}
                </span>
                {canEdit && (
                  <button type="button" className="btn-secondary btn-sm" onClick={() => onRestoreVersion(idx)}>
                    Restore this version
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {stages.length > 0 && (
        <div className="progress-card">
          <h3>Progress</h3>
          <div className="progress-stages">
            {stages.map((s) => {
              const entry = sub.progress?.[s.id];
              const done = !!entry?.done;
              return (
                <label key={s.id} className={"progress-stage " + (done ? "done" : "")}>
                  <input
                    type="checkbox"
                    checked={done}
                    disabled={!canEdit}
                    onChange={(e) => toggleStage(s.id, e.target.checked)}
                  />
                  <span className="progress-stage-label">{s.label}</span>
                  {entry?.updatedAt && (
                    <span className="progress-stage-meta">
                      {new Date(entry.updatedAt).toLocaleDateString()}
                      {entry.updatedBy?.userDetails && ` · ${entry.updatedBy.userDetails}`}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {!editing ? (
        <ReadOnlySubmission data={sub.data} />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSave)} noValidate>
            {formConfig.map((sec) => (
              <section key={sec.title} className="edit-section">
                <h3 className="edit-section-title">{sec.title}</h3>
                {sec.fields.map((f) => <FieldRenderer key={f.id} field={f} />)}
              </section>
            ))}
            <div className="actions">
              <button type="submit" className="btn-save" disabled={methods.formState.isSubmitting}>Save changes</button>
              <button type="button" className="btn-secondary" onClick={cancelEdit}>Cancel</button>
            </div>
          </form>
        </FormProvider>
      )}

      {showEmail && (
        <div className="modal-overlay" onClick={() => !emailBusy && setShowEmail(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Email this submission</h3>
            <p className="hint">Configured defaults and matching recipient rules are included automatically. Search people or type an additional email address, then press Enter to add it.</p>
            <RecipientEditor recipients={emailRecipients} onChange={setEmailRecipients} />
            <label className="field-label" style={{ marginTop: "0.75rem" }}>
              Note (optional)
              <textarea
                rows={3}
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                placeholder="Add a short message to include above the submission…"
                style={{ width: "100%" }}
              />
            </label>
            {emailStatus && <div className="hint" style={{ marginTop: "0.5rem" }}>{emailStatus}</div>}
            <div className="actions" style={{ marginTop: "1rem" }}>
              <button type="button" className="btn-save" disabled={emailBusy} onClick={sendEmail}>
                {emailBusy ? "Sending…" : "Send"}
              </button>
              <button type="button" className="btn-secondary" disabled={emailBusy} onClick={() => setShowEmail(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
